/* eslint-disable react/destructuring-assignment */
import { SxProps, Theme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReturnIcon from '@mui/icons-material/KeyboardReturn';
import { OrderStatus, PaymentStatus, TransferStatus } from '@prisma/client';
import { OrderWithPayments } from '../../types/orders';
import { Transaction } from '../../interfaces';
import Badge, { ColorScheme } from '../global/Badge';

type Props = {
  tooltipOffset?: [number, number];
  light?: boolean;
  sx?: SxProps<Theme>;
} & (
  | {
      order: OrderWithPayments;
    }
  | {
      transaction: Transaction;
    }
  | {
      status: OrderStatus | PaymentStatus | TransferStatus;
      tooltipText?: string;
    }
);

const getColorScheme = (
  status: OrderStatus | PaymentStatus | TransferStatus | undefined,
): ColorScheme => {
  switch (status) {
    case 'COMPLETED':
    case 'SUCCESS':
    case 'CREATED':
      return 'secondary';
    case 'FAILED':
      return 'primary';
    case 'PENDING':
    case 'INCOMPLETE':
    case 'REFUNDED':
    case 'REVERSED':
    case 'CANCELED':
    case 'VOIDED':
    default:
      return 'grey';
  }
};

export default function TransactionBadge({
  tooltipOffset = [0, -10],
  light = true,
  sx,
  ...props
}: Props) {
  const order = 'order' in props ? props.order : null;
  const transaction = 'transaction' in props ? props.transaction : null;
  const forcedStatus = 'status' in props ? props.status : null;

  let status = forcedStatus || order?.status || transaction?.status;
  if ('transaction' in props && status === 'PENDING' && transaction?.error) {
    status = 'FAILED';
  }

  let badgeText = '';
  let badgeIcon = null;
  let tooltipText = '';

  switch (status) {
    case 'COMPLETED':
    case 'SUCCESS':
    case 'CREATED':
      badgeText = 'Succeeded';
      badgeIcon = CheckIcon;
      tooltipText = 'This payment is complete.';
      break;
    case 'FAILED':
      badgeText = 'Failed';
      badgeIcon = CloseIcon;
      tooltipText = `Payment failed. ${
        'order' in props ? order?.payments?.[0]?.error || '' : ''
      }${
        'transaction' in props && transaction?.error ? transaction.error : ''
      }`;
      break;
    case 'PENDING':
    case 'INCOMPLETE':
      badgeText = 'Incomplete';
      badgeIcon = AccessTimeIcon;
      tooltipText = 'The customer has not entered their payment method.';
      break;
    case 'REFUNDED':
    case 'REVERSED': {
      badgeText = status === 'REFUNDED' ? 'Refunded' : 'Reversed';
      badgeIcon = ReturnIcon;
      tooltipText =
        status === 'REFUNDED'
          ? 'The refund has been initiated.'
          : 'The transfer has been reversed.';
      break;
    }
    case 'CANCELED':
      badgeText = 'Canceled';
      badgeIcon = CloseIcon;
      tooltipText = 'This transfer was canceled.';
      break;
    case 'VOIDED':
      badgeText = 'Voided';
      badgeIcon = CloseIcon;
      tooltipText = 'This payment was voided.';
      break;
    default:
      return null;
  }

  if ('tooltipText' in props && props.tooltipText)
    tooltipText = props.tooltipText;

  return (
    <Badge
      text={badgeText}
      icon={badgeIcon}
      light={light}
      sx={sx}
      tooltip={{
        text: tooltipText,
        offset: tooltipOffset,
      }}
      colorScheme={getColorScheme(status)}
    />
  );
}
